import React, { ReactNode } from 'react'
import { H3, H4, P } from '@/components/common/Typo'
import Image from 'next/image'
import useGetProductImagePath from '@/hooks/useGetProductImagePath'
import useAppContext from '@/hooks/useAppContext'
import { useTranslate } from '@tolgee/react'
import useProductImageState from '@/hooks/useProductImageState'
import CartProgressBars from '@/components/common/ProgressBar/CartProgressBar'
import Button from '@/components/common/Button'
import MenuLogo from '@/components/common/MenuLogo'
import { OrderItemImage } from '@/components/common/OrderItem/Elements'

const Item = ({
  name,
  quantity,
  price,
  imageUrl,
}: {
  name: string
  quantity: number
  price: number | null
  imageUrl?: string
}): ReactNode => {
  const { getProductImagePath } = useGetProductImagePath()
  const { formatMoney } = useAppContext()
  const { showImage, showDefaultImage } = useProductImageState()

  const imageSrc = getProductImagePath(imageUrl, showDefaultImage, {
    shouldCache: true,
    containerWidth: 120,
    containerHeight: 120,
  })

  return (
    <div className="mb-2.5 flex">
      {showImage && imageSrc && (
        <>
          {imageSrc.includes('default-product') ? (
            <div className="flex flex-col justify-start">
              <div className="[&>*]:opacity-12 mr-2.5 flex h-20 w-20 items-center justify-center rounded-lg bg-[#f5f8fc]">
                <MenuLogo
                  showStoreName={false}
                  truncate
                  className="max-w-16"
                  logoClassName="md:max-h-[40px]"
                />
              </div>
            </div>
          ) : (
            <div className="mr-2.5 flex flex-col justify-start">
              <Image
                width={80}
                height={80}
                className="min-w-20 object-cover pr-2.5"
                src={imageSrc}
                alt={name}
              />
            </div>
          )}
        </>
      )}
      <div className="flex grow flex-col justify-between">
        <div className="flex justify-between">
          <H4 bold margin="none">
            {name}
          </H4>
          <div className="self-end text-nowrap">
            <P margin="none">{`x${quantity}`}</P>
          </div>
        </div>
        {price && <P margin="none">{formatMoney(price)}</P>}
      </div>
    </div>
  )
}

const AddToCartToast = ({
  items,
  onClickGoCart,
}: {
  items: {
    name: string
    quantity: number
    price: number | null
    imageUrl?: string
  }[]
  onClickGoCart?: () => void
}): ReactNode => {
  const { t } = useTranslate()

  return (
    <>
      <CartProgressBars />
      <div className="p-2.5">
        <H3 bold>{t('Item(s) Added')}</H3>
        {items.map(({ name, quantity, price, imageUrl }) => (
          <Item
            key={name}
            name={name}
            quantity={quantity}
            price={price}
            imageUrl={imageUrl}
          />
        ))}
        <Button className="w-full" onClick={onClickGoCart} secondary>
          {t('View Cart')}
        </Button>
      </div>
    </>
  )
}

export default AddToCartToast
